import AnimatedSvg from '@/atoms/AnimatedSvg'
import LeftCol from '@/atoms/LeftCol'
import MediumTitle from '@/atoms/MediumTitle'
import Paragraph from '@/atoms/Paragraph'
import RightCol from '@/atoms/RightCol'
import SpacedRow from '@/atoms/SpacedRow'
import {
  ServicesSoftwareDevelopmentPage,
  ServicesSoftwareDevelopmentPage_ServicesRows,
} from '@/content/types'
import { useLocale } from '@/hooks/useLocale'
import Head from 'next/head'
import MetaInformation from '@/utils/MetaInformation'
import AdaptiveThemeBox from '@/atoms/AdaptiveThemeBox'
import HeaderBar from '@/organisms/HeaderBar'
import ThemeBox from '@/atoms/ThemeBox'
import ThemedBackground from '@/atoms/ThemedBackground'
import Container from '@/molecules/Container'
import DoubleHeader from '@/molecules/DoubleHeader'
import Breadcrumbs from '@/molecules/Breadcrumbs'
import Image from 'next-export-optimize-images/image'
import Footer from '@/organisms/Footer'
import SeparatorHeader from '@/atoms/SeparatorHeader'
import Cases from '@/molecules/Cases'
import UnderlinedLink from '@/atoms/UnderlinedLink'
import ShadowCard from '@/atoms/ShadowCard'
import FadeWrapper from '@/molecules/FadeWrapper'
import Quote from '@/molecules/Quote'
import QuoteAuthor from '@/atoms/QuoteAuthor'
import ContactCTA from './ContactCTA'
import NextLink from 'next/link'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleRight } from '@fortawesome/free-solid-svg-icons'
import WayOfWorking from './WayOfWorking'
import Img from '@/atoms/Img'
import ImageGrid from '@/atoms/ImageGrid'

type Props = {
  attributes: any
}

export const makeServiceRow = (
  service: ServicesSoftwareDevelopmentPage_ServicesRows,
  i: number,
) => {
  const imageCol = (
    <div className="h-full flex flex-col justify-center align-end relative min-h-[14rem]">
      {service.static_image ? (
        <Img className="lg:max-h-[24rem] object-contain" src={service.image} />
      ) : (
        <AnimatedSvg
          wrapper_id={service.image}
          img_src={service.image}
          className={`absolute w-[1800px] md:w-[2200px] ${
            i % 2 === 0 ? 'right-0' : 'left-0'
          }`}
        />
      )}
    </div>
  )

  const content = (
    <>
      <MediumTitle>{service.title}</MediumTitle>
      <Paragraph className="text-sm leading-loose">{service.content}</Paragraph>
    </>
  )

  if (i % 2 === 0) {
    return (
      <SpacedRow key={i}>
        {imageCol}
        <RightCol>{content}</RightCol>
      </SpacedRow>
    )
  } else {
    return (
      <SpacedRow key={i}>
        <LeftCol below className="order-last lg:order-first">
          {content}
        </LeftCol>
        {imageCol}
      </SpacedRow>
    )
  }
}

export default function ServicesTemplate({ attributes }: Props) {
  const { translations, locale } =
    useLocale<ServicesSoftwareDevelopmentPage>(attributes)

  return (
    <>
      <Head>
        <MetaInformation locale={locale} {...translations.meta} />
        <title>{translations.page_title}</title>
      </Head>
      <AdaptiveThemeBox defaultTheme="light">
        <HeaderBar />
      </AdaptiveThemeBox>
      <ThemeBox theme="light">
        <ThemedBackground className="xl:pb-20 overflow-x-hidden">
          <Container>
            <SpacedRow>
              <LeftCol below className="lg:-mr-16">
                <DoubleHeader
                  upper={
                    <Breadcrumbs
                      pathItems={[
                        {
                          title: translations.overview.parent_title,
                          href: '/services',
                        },
                        { title: translations.overview.upper_title },
                      ]}
                    />
                  }
                  main={translations.overview.large_title}
                />
                <p className="text-sm leading-loose">
                  {translations.overview.intro_text}
                </p>
              </LeftCol>
              <RightCol above className="lg:-ml-12 grid">
                <Image
                  src={translations.overview.intro_image}
                  alt={translations.overview.intro_image}
                  width={520}
                  height={520}
                  className="justify-self-center"
                />
              </RightCol>
            </SpacedRow>
          </Container>
          <Container>
            <div className="py-8 md:py-10 lg:py-14 lg:px-30">
              <SeparatorHeader
                title={translations.services_icons.title}
                subtitle={translations.services_icons.subtitle}
                extraClasses="pb-8 lg:pb-20 lg:pt-6"
                subtitleOnTop
              />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 lg:gap-5">
                {translations.services_icons.icon_cards.map((card) => (
                  <ShadowCard key={card.title}>
                    <div className="w-16 md:w-24 justify-self-center">
                      <Image
                        src={card.image}
                        width={96}
                        height={96}
                        alt={card.title}
                        className="block"
                      />
                    </div>
                    <p className="text-xs md:text-normal font-medium text-center max-w-[8rem] justify-self-center">
                      {card.title}
                    </p>
                  </ShadowCard>
                ))}
              </div>
            </div>
            {translations.services_rows.map(makeServiceRow)}
          </Container>
        </ThemedBackground>
        <ThemedBackground>
          <Container>
            <section className="py-16 text-black bg-white xl:py-36 black:bg-black black:text-white light:bg-gray-700">
              <SeparatorHeader
                title={translations.way_of_working_title}
                extraClasses="lg:mb-16"
              />
              <Container>
                <WayOfWorking />
              </Container>
            </section>
            {translations.stacks && (
              <section className="text-black bg-white black:bg-black black:text-white light:bg-gray-700">
                <SeparatorHeader
                  title={translations.stacks.title}
                  extraClasses="py-16"
                />
                <div className="flex justify-center">
                  <p className="max-w-4xl leading-loose">
                    {translations.stacks.description}
                  </p>
                </div>
                <div className="flex justify-center mt-16 pb-16">
                  <ImageGrid images={translations.stacks.stack_cards} />
                </div>
              </section>
            )}
            {/* TODO: Implement new designs once they are available. comment out for now */}
            {/* <section className="mb-16 y-8 md:py-10 lg:py-14 lg:px-30">
              <SeparatorHeader
                title={translations.more_services.title}
                extraClasses="py-16"
              />
              <div className="grid md:grid-cols-3 justify-center gap-4 md:gap-8">
                {translations.more_services.more_services_cards.map((card) => (
                  <NextLink
                    key={card.text}
                    href={card.href}
                    className="cursor-pointer"
                  >
                    <ShadowCard extraClassName="overflow-hidden group hover:shadow-xl hover:-translate-y-3 transition duration-300">
                      <div className="justify-self-center p-8">
                        <Image
                          src={card.image}
                          width={200}
                          height={200}
                          alt={card.text}
                          className="block"
                        />
                      </div>
                      <div className="bg-blue hover:bg-blue-hover py-3 px-4 flex gap-4 items-center justify-between flex-row-reverse text-white transition duration-300">
                        <FontAwesomeIcon icon={faAngleRight} className="h-4" />
                        <span className="text-xs lg:text-normal">
                          {card.text}
                        </span>
                      </div>
                    </ShadowCard>
                  </NextLink>
                ))}
              </div>
            </section> */}
          </Container>
        </ThemedBackground>
      </ThemeBox>
      {translations.quote && (
        <ThemeBox theme="black">
          <ThemedBackground>
            <Container className="py-8 lg:pt-16">
              <FadeWrapper>
                <div className="my-8 text-center lg:mx-24 lg:mb-24">
                  <Quote
                    from={
                      <QuoteAuthor
                        img={translations.quote.author_image}
                        title={translations.quote.author_title}
                        from={translations.quote.author_name}
                      />
                    }
                  >
                    {translations.quote.quote_text}
                  </Quote>
                </div>
              </FadeWrapper>
            </Container>
          </ThemedBackground>
        </ThemeBox>
      )}
      <ThemeBox theme="bright">
        <ThemedBackground>
          <Container>
            <section className="text-black bg-white black:bg-black black:text-white light:bg-gray-700">
              <SeparatorHeader
                title={translations.cases.title}
                subtitle={translations.cases.subtitle}
                extraClasses="py-16 lg:pb-20 lg:pt-36"
              />
              <Cases cases={translations.cases.cases} locale={locale} />
              <div className="mt-20 text-center">
                <UnderlinedLink
                  className="text-sm"
                  title={translations.readmore_cases}
                  href="/projects"
                />
              </div>
            </section>
            <ContactCTA />
          </Container>
        </ThemedBackground>
      </ThemeBox>
      <ThemeBox theme="black">
        <Footer />
      </ThemeBox>
    </>
  )
}
